import { z } from 'zod';
import { ADMIN_ROLES } from './shared.js';
/**
 * The admin permission registry — the single list of permission strings used
 * anywhere in the admin realm (NipigeV3 pattern: never free-type a permission
 * string in service or console code; add it here first).
 *
 * Permissions are the ONLY authorization primitive. Guards check a principal's
 * permission set; the console gates navigation and routes on permissions; role
 * NAMES are never consulted for access anywhere. Roles — built-in or custom —
 * are nothing but named sets drawn from this registry.
 *
 * There is deliberately no database representation of this list: the registry
 * is code, reviewed like code. Only a CUSTOM role's chosen subset is stored
 * (one document per role), and it is validated against this registry on every
 * write.
 */
export const ADMIN_PERMISSIONS = [
    // ── platform — RESERVED (see RESERVED_ADMIN_PERMISSIONS below) ──────────
    'platform:admin:manage', // create/suspend admin accounts, change their role
    'platform:role:manage', // author, edit and delete custom roles
    // ── platform — grantable oversight ──────────────────────────────────────
    'platform:audit:read',
    // ── moderation ──────────────────────────────────────────────────────────
    'moderation:report:manage', // queue, context, claim, action
    'moderation:appeal:manage',
    // ── members ─────────────────────────────────────────────────────────────
    'member:read', // search, profile, activity
    'member:sanction:read', // current sanction state (the read-only context §4.1 grants support)
    'member:sanction:manage', // suspend / reinstate
    'member:export', // GDPR-style data export
    'member:delete', // close / restore an account (FR-ADM-41) — soft, see the route
    // ── standing (star badges / tiers) ──────────────────────────────────────
    'family:milestone:review',
    'family:awards:manage',
    'family:awards:review',
    'standing:upgrade:manage', // queue + decide (the >3★ gate, docs/02 §11)
    'standing:tier:manage', // thresholds and tier config
    'standing:tier:grant', // grant a tier directly to a member
    // ── commerce ────────────────────────────────────────────────────────────
    'payment:read',
    'payment:refund',
    // ── support ─────────────────────────────────────────────────────────────
    'support:ticket:manage',
    // ── content & notifications ─────────────────────────────────────────────
    'content:policy:manage',
    'content:faq:manage',
    'notification:template:manage',
    'notification:broadcast:send',
    // ── analytics ───────────────────────────────────────────────────────────
    'analytics:dashboard:read',
    'onboarding:publish',
];
export const AdminPermission = z.enum(ADMIN_PERMISSIONS);
/**
 * Never grantable to a custom role, enforced twice: excluded from the
 * GrantablePermission schema (a role document can never store them) and
 * hard-refused by AdminRoleGuard for custom principals even if a document
 * somehow carried one. The escalation this closes is the classic one (see
 * NipigeV3 docs/tenant-custom-roles.md): a role that can manage roles or
 * admins can mint itself any other power, so those two capabilities stay with
 * the built-in Super Admin.
 */
export const RESERVED_ADMIN_PERMISSIONS = [
    'platform:admin:manage',
    'platform:role:manage',
];
export const GRANTABLE_ADMIN_PERMISSIONS = ADMIN_PERMISSIONS.filter((p) => !RESERVED_ADMIN_PERMISSIONS.includes(p));
/** What a custom role may hold — the registry minus the reserved pair. */
export const GrantablePermission = z.enum(GRANTABLE_ADMIN_PERMISSIONS);
/**
 * Route → required permission (plus the two auth phases):
 *
 * - `'public'`      — no session at all. Login only: it is the endpoint that
 *                     CREATES a session, so it cannot demand one.
 * - `'mfa-pending'` — an authenticated principal whose MFA is not (yet)
 *                     verified. Deliberately the narrowest capability in the
 *                     system: it exists so the two MFA-completion endpoints
 *                     are reachable from a step-1 token, and for nothing else.
 *
 * This is the single source of truth for what each endpoint demands. The
 * API's guards resolve every request through it (via the route object bound
 * by @Contract), a boot-time check refuses to start the API if any /admin
 * handler is not covered by it, and the console derives navigation and route
 * gates from the same permissions.
 *
 * `satisfies Record<keyof typeof adminRoutes, …>` makes the table exhaustive
 * by construction: adding a route to the manifest without deciding its access
 * — or leaving a stale entry behind — is a compile error, not a gap.
 */
export const adminAccess = {
    awardSeasons: 'family:awards:manage',
    createAwardSeason: 'family:awards:manage',
    updateAwardSeason: 'family:awards:manage',
    awardEntries: 'family:awards:review',
    marriageReviews: 'family:milestone:review',
    marriageVerification: 'family:milestone:review',
    decideMarriage: 'family:milestone:review',
    // auth
    login: 'public',
    mfaEnrol: 'mfa-pending',
    mfaVerify: 'mfa-pending',
    // admins & audit
    admins: 'platform:admin:manage',
    createAdmin: 'platform:admin:manage',
    suspendAdmin: 'platform:admin:manage',
    setAdminRole: 'platform:admin:manage',
    audit: 'platform:audit:read',
    // custom roles
    roles: 'platform:role:manage',
    createRole: 'platform:role:manage',
    updateRole: 'platform:role:manage',
    deleteRole: 'platform:role:manage',
    // moderation
    reports: 'moderation:report:manage',
    reportContext: 'moderation:report:manage',
    claimReport: 'moderation:report:manage',
    moderate: 'moderation:report:manage',
    sanction: 'member:sanction:read',
    // appeals
    appeals: 'moderation:appeal:manage',
    decideAppeal: 'moderation:appeal:manage',
    // subscriptions / standing
    upgradeQueue: 'standing:upgrade:manage',
    decideUpgrade: 'standing:upgrade:manage',
    tierConfig: 'standing:tier:manage',
    grantTier: 'standing:tier:grant',
    // members
    members: 'member:read',
    member: 'member:read',
    memberActivity: 'member:read',
    memberExport: 'member:export',
    suspendMember: 'member:sanction:manage',
    reinstateMember: 'member:sanction:manage',
    // Closing an account is deliberately NOT part of member:sanction:manage.
    // A moderator's job is to stop a member misbehaving, which suspension and
    // banning already do; ending the account outright reaches payments,
    // donations and contribution records that NFR-08 says must survive, so it
    // sits with the built-in Super Admin alone (docs/01 §4.1 separation of
    // duties). Granting it to a custom role remains possible and deliberate.
    deleteMember: 'member:delete',
    restoreMember: 'member:delete',
    // payments
    payments: 'payment:read',
    refund: 'payment:refund',
    // support
    tickets: 'support:ticket:manage',
    ticketReply: 'support:ticket:manage',
    // content & broadcast
    publishPolicy: 'content:policy:manage',
    upsertFaq: 'content:faq:manage',
    upsertTemplate: 'notification:template:manage',
    broadcastPreview: 'notification:broadcast:send',
    broadcast: 'notification:broadcast:send',
    // analytics
    dashboard: 'analytics:dashboard:read',
    publishOnboardingFlow: 'onboarding:publish',
};
/**
 * The four built-in roles of docs/01-PRD.md §4.1, as permission sets — pure
 * code, never seeded into or read from the database. A role change to one of
 * these is a reviewed deploy, exactly like a change to the registry itself.
 * `packages/contracts/src/admin/rbac.test.ts` pins the induced route access
 * to the §4.1 separation-of-duties table, so these sets cannot drift from
 * the PRD silently.
 */
export const BUILTIN_ROLE_PERMISSIONS = {
    super: ADMIN_PERMISSIONS,
    moderator: [
        'moderation:report:manage',
        'moderation:appeal:manage',
        'member:read',
        'member:sanction:read',
        'member:sanction:manage',
        'analytics:dashboard:read',
    ],
    support: [
        'member:read',
        'member:sanction:read',
        'member:export',
        'support:ticket:manage',
        'analytics:dashboard:read',
    ],
    finance: ['platform:audit:read', 'payment:read', 'payment:refund', 'analytics:dashboard:read'],
};
/** The built-in roles that hold a permission — used by golden tests and displays. */
export const rolesHolding = (permission) => ADMIN_ROLES.filter((role) => BUILTIN_ROLE_PERMISSIONS[role].includes(permission));
/** Console display names; grouped for the role-editor checkboxes. */
export const ADMIN_PERMISSION_LABELS = {
    'family:milestone:review': 'Review private marriage verification',
    'family:awards:manage': 'Manage Family award seasons',
    'family:awards:review': 'Read submitted Family award entries',
    'platform:admin:manage': 'Manage admin accounts (reserved)',
    'platform:role:manage': 'Manage roles (reserved)',
    'platform:audit:read': 'Read the audit log',
    'moderation:report:manage': 'Work the report queue',
    'moderation:appeal:manage': 'Decide appeals',
    'member:read': 'Look up members',
    'member:sanction:read': 'See sanction state',
    'member:sanction:manage': 'Suspend / reinstate members',
    'member:export': 'Export member data',
    'member:delete': 'Close / restore member accounts',
    'standing:upgrade:manage': 'Decide star-badge upgrades',
    'standing:tier:manage': 'Configure tiers & thresholds',
    'standing:tier:grant': 'Grant a tier directly',
    'payment:read': 'View payments',
    'payment:refund': 'Issue refunds',
    'support:ticket:manage': 'Work support tickets',
    'content:policy:manage': 'Publish policies',
    'content:faq:manage': 'Edit the FAQ',
    'notification:template:manage': 'Edit notification templates',
    'notification:broadcast:send': 'Send broadcasts',
    'analytics:dashboard:read': 'View the dashboard',
    'onboarding:publish': 'Publish onboarding flow',
};
export const ADMIN_PERMISSION_GROUPS = [
    { label: 'Moderation', permissions: ['moderation:report:manage', 'moderation:appeal:manage'] },
    {
        label: 'Members',
        permissions: [
            'member:read',
            'member:sanction:read',
            'member:sanction:manage',
            'member:export',
            'member:delete',
        ],
    },
    {
        label: 'Standing',
        permissions: ['standing:upgrade:manage', 'standing:tier:manage', 'standing:tier:grant'],
    },
    { label: 'Commerce', permissions: ['payment:read', 'payment:refund'] },
    { label: 'Support', permissions: ['support:ticket:manage'] },
    {
        label: 'Content & notifications',
        permissions: [
            'content:policy:manage',
            'content:faq:manage',
            'notification:template:manage',
            'notification:broadcast:send',
        ],
    },
    { label: 'Platform', permissions: ['platform:audit:read', 'analytics:dashboard:read'] },
    {
        label: 'Family',
        permissions: ['family:milestone:review', 'family:awards:manage', 'family:awards:review'],
    },
    { label: 'Onboarding', permissions: ['onboarding:publish'] },
];
//# sourceMappingURL=rbac.js.map